import {
  useRequiredTenantIdFromRoute,
  useTenantIdFromRoute,
} from "src/core/hooks/routing/useTenantIdFromRoute";
import { tenantPaths } from "src/core/utils/route";

/**
 * Hooks utilitaires de génération de routes tenant-scoped.
 *
 * Objectifs :
 * - Centraliser la construction des URLs tenant-aware.
 * - Éviter toute concaténation manuelle de routes dans les composants.
 * - Garantir la cohérence des chemins dans toute l'application.
 *
 * Ces hooks reposent UNIQUEMENT sur le tenantId présent dans l'URL.
 */

type TenantPaths = {
  home: (tenantIdOverride?: string) => string;
  folders: () => string;
  folder: (id: string) => string;
  import: () => string;

  // Routes d'erreur tenant-scoped
  error403: () => string;
  error404: () => string;
  error500: () => string;
};

/**
 * useTenantPaths (SAFE) :
 * Variante défensive.
 *
 * - Retourne `null` si le tenantId n'est pas présent dans l'URL.
 * - À utiliser dans :
 *   - Header
 *   - Layouts partagés
 *   - Composants affichés avant validation complète du routing
 *
 * Objectif :
 * - Éviter des crashs dans des zones non strictement tenant-scoped.
 */
export const useTenantPaths = (): TenantPaths | null => {
  const tenantId = useTenantIdFromRoute();

  if (!tenantId) {
    return null;
  }

  return {
    home: () => tenantPaths.home(tenantId),
    folders: () => tenantPaths.folders(tenantId),
    folder: (id: string) => tenantPaths.folder(tenantId, id),
    import: () => tenantPaths.import(tenantId),

    error403: () => tenantPaths.error403(tenantId),
    error404: () => tenantPaths.error404(tenantId),
    error500: () => tenantPaths.error500(tenantId),
  };
};

/**
 * useRequiredTenantPaths (STRICT) :
 * Variante stricte.
 *
 * - Lance une erreur si le tenantId est absent de l'URL.
 * - À utiliser UNIQUEMENT dans des pages tenant-scoped,
 *   protégées en amont par TenantParamGuard.
 *
 * Garanties :
 * - Simplifie le code des pages (pas de checks défensifs).
 * - Fait échouer rapidement si l'architecture de routing est violée.
 */
export const useRequiredTenantPaths = (): TenantPaths => {
  const tenantId = useRequiredTenantIdFromRoute();

  return {
    home: (tenantIdOverride) => tenantPaths.home(tenantIdOverride ?? tenantId),
    folders: () => tenantPaths.folders(tenantId),
    folder: (id: string) => tenantPaths.folder(tenantId, id),
    import: () => tenantPaths.import(tenantId),

    error403: () => tenantPaths.error403(tenantId),
    error404: () => tenantPaths.error404(tenantId),
    error500: () => tenantPaths.error500(tenantId),
  };
};
